import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { ITcProductCertifiedWeightInformation, ITcProductFacilitiesInformation, ITcProductFirstProcessorInformation, ITcProductRawMaterialGeographicInformation, ITcProductRawMaterialInformation, ProductFormData } from "../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { ApiShipmentResponseItem } from "../shipments/shipments.model";

// ===== INTERFACE DEFINITIONS =====

export interface ProductShipmentResponse {
  shipmentId: string;
  productId: string;
  tcId: string;
  tcProductShipmentNo: string[];
  tcProductNo: string;
  tcProductCategoryCode: string;
  tcNetShippingWeight: number;
  tcProductProductionDate: string; // Format: YYYY-MM-DD
  tcProductSupplementaryWeight: number;
  tcProductOrderNo: string[];
  tcProductArticleNo: string;
  tcProductNumberOfUnits: number;
  tcProductUnitsMetric: string;
  tcProductInfoAssociatedTc: string;
  tcAssociatedProductShipmentNo?: string[]; // Changed from string to string[]
}

// Input interface for the product data coming from the API (stepData)
export interface InputProductData {
  productInfo: {
    tcProductShipmentNo: string[];
    tcProductNo: string;
    tcProductCategoryCode: string;
    tcProductProductionDate: string;
    tcNetShippingWeight: string;
    tcProductSupplementaryWeight: string;
    tcProductSupplementaryWeightInfo: string; // Added missing field
    tcProductOrderNo: string[];
    tcProductArticleNo: string;
    tcProductNumberOfUnits: string;
    tcProductUnitsMetric: string;
    tcProductInfoAssociatedTc: string;
    tcProductOrder: string;
    tcProductCategoryUserSpecificTerm: string;
    tcProductDetailCode: string;
    tcProductDetailUserSpecificTerm: string;
  };

  rawMaterialInfo: Array<{
    tcProductRawMaterialOrder: number;
    tcProductRawMaterialCode: string;
    tcProductRawMaterialPercentage: string;
    tcProductRawMaterialUserSpecificTerm?: string;
    tcProductRawMaterialCodeDescription?: string; // Added missing field
  }>;

  geographicInfo: Array<{
    tcProductCertifiedRawMaterialCode: string; // Updated field names to match API
    tcProductCertifiedRawMaterialCodeDescription?: string;
    tcProductCertifiedRawMaterialWeight: string;
    tcProductCertifiedRawMaterialCountryOrArea: string; // Updated field name
    tcProductCertifiedRawMaterialCountryOrAreaDescription?: string;
    tcProductCertifiedRawMaterialStateOrProvince: string; // Updated field name
    tcProductCertifiedRawMaterialStateOrProvinceDescription?: string;
  }>;

  additionalInfo: {
    tcProductInfoFiberLength: string;
    tcProductInfoYarnCount: string;
    tcProductInfoFabricWeightAndConstruct: string;
    tcProductInfoFabricWeightOfFinishedProduct: string;
    tcProductGtin: string;
    tcProductInfoAdditional: string;
    tcProductAdditives: string | string[]; // Allow both string and array
    tcProductInfoTechnicalSpecifications: string;
    tcProductAdditivesWeight: string | number | null; // Allow multiple types
    tcProductFeedStock: string | string[]; // Now supports array (chip input)
    tcProductPercentTextileWaste: string;
    tcProductPackagingFor: string;
    tcProductMaterialReclaimedSource: string[]; // Now supports array (chip input)
    tcProductInfoTrimsExcluded?: string; // Made optional
    tcProductOriginFacilityTeId?: string[] | string | null;
  };

  productCertifiedWeightInfo: Array<{
    tcProductClaimsCategory: string;
    tcProductCertifiedWeight: string | number;
    tcProductRawMaterialCodeClaimsCategory?: string; // Added missing fields
    tcProductRawMaterialCodeClaimsCategoryDescription?: string;
  }>;

  productFacilitiesInfo: Array<{
    tcProductFacilitiesTeId: string;
    tcProductFacilitiesProcessCode: string;
  }>;

  productFirstProcessorInfo: Array<{
    tcProductFirstProcessorTeId: string;
    tcProductFirstProcessCode: string; // Fixed field name
  }>;
}

// Output interface for the mapped payload
export interface MappedProductPayload {
  tcId: string;
  tcProductOrderNo: string[];
  tcProductArticleNo: string;
  tcProductProductionDate: string;
  tcProductShipmentNo: string[];
  tcProductNumberOfUnits: string;
  tcProductUnitsMetric: string;
  tcProductNetShippingWeight: number;
  tcProductSupplementaryWeight: number;
  tcProductInfoSupplementaryWeight: string;
  tcProductCategoryCode: string;
  tcProductCategoryUserSpecificTerm: string;
  tcProductDetailCode: string;
  tcProductDetailUserSpecificTerm: string;

  // Raw Material Information
  tcProductRawMaterialInformation: Array<{
    tcProductRawMaterialCode: string;
    tcProductRawMaterialPercentage: number;
    tcProductRawMaterialUserSpecificTerm: string;
    tcProductRawMaterialCodeDescription: string;
  }>;

  // Geographic Information
  tcProductCertifiedRawMaterialGeographicInfo: Array<{
    tcProductCertifiedRawMaterialCode: string;
    tcProductCertifiedRawMaterialCodeDescription: string;
    tcProductCertifiedRawMaterialWeight: number;
    tcProductCertifiedRawMaterialCountryOrArea: string;
    tcProductCertifiedRawMaterialCountryOrAreaDescription: string;
    tcProductCertifiedRawMaterialStateOrProvince: string;
    tcProductCertifiedRawMaterialStateOrProvinceDescription: string;
  }>;

  // Certified Weight Information
  tcProductCertifiedWeightInformation: Array<{
    tcProductClaimsCategory: string;
    tcProductCertifiedWeight: number;
    tcProductRawMaterialCodeClaimsCategory: string;
    tcProductRawMaterialCodeClaimsCategoryDescription: string;
  }>;

  // Facilities Information
  tcProductFacilitiesInformation: Array<{
    tcProductFacilitiesTeId: string;
    tcProductFacilitiesProcessCode: string;
  }>;

  // First Processor Information
  tcProductFirstProcessorInformation: Array<{
    tcProductFirstProcessorTeId: string;
    tcProductFirstProcessCode: string;
  }>;

  // Additional Information
  tcProductAdditives: string;
  tcProductAdditivesWeight: number;
  tcProductInfoFiberLength: string;
  tcProductInfoYarnCount: string;
  tcProductInfoFabricWeightAndConstruct: string;
  tcProductInfoFabricWeightOfFinishedProduct: string;
  tcProductInfoTechnicalSpecifications: string;
  tcProductFeedstock: string[]; // Now array for chip input
  tcProductPercentTextileWaste: string;
  tcProductPackagingFor: string;
  tcProductGtin: string;
  tcProductReclaimedMaterialSource: string[]; // Now array for chip input
  tcProductInfoAdditional: string;
  tcProductOriginFacilityTeId: string[];
  
  // Description fields
  tcProductCategoryCodeDescription: string;
  tcProductDetailCodeDescription: string;

  // Optional productId for editing
  productId?: string;
}

// ===== UTILITY FUNCTIONS =====

/**
 * Enhanced mapProductData function with proper type handling
 */
export const mapProductData = (
  stepData: any,
  tcId: string,
  shipmentDocNo?: string[],
  productId?: string | null
): MappedProductPayload => {
  const { productInfo, rawMaterialInfo, geographicInfo, productCertifiedWeightInfo, 
          productFacilitiesInfo, productFirstProcessorInfo, additionalInfo } = stepData;

  return {
    tcId: tcId,
    tcProductOrderNo: Array.isArray(productInfo.tcProductOrderNo) 
      ? productInfo.tcProductOrderNo 
      : productInfo.tcProductOrderNo ? [productInfo.tcProductOrderNo] : [],
    tcProductArticleNo: productInfo.tcProductArticleNo || "",
    tcProductProductionDate: productInfo.tcProductProductionDate || "",
    tcProductShipmentNo: Array.isArray(productInfo.tcProductShipmentNo)
      ? productInfo.tcProductShipmentNo
      : productInfo.tcProductShipmentNo ? [productInfo.tcProductShipmentNo] : [],
    tcProductNumberOfUnits: String(productInfo.tcProductNumberOfUnits || "0"),
    tcProductUnitsMetric: Array.isArray(productInfo.tcProductUnitsMetric)
      ? productInfo.tcProductUnitsMetric[0] || ""
      : productInfo.tcProductUnitsMetric || "",
    tcProductNetShippingWeight: parseFloat(productInfo.tcNetShippingWeight || "0"),
    tcProductSupplementaryWeight: parseFloat(productInfo.tcProductSupplementaryWeight || "0"),
    tcProductInfoSupplementaryWeight: productInfo.tcProductSupplementaryWeightInfo || "",
    tcProductCategoryCode: productInfo.tcProductCategoryCode || "",
    tcProductCategoryUserSpecificTerm: productInfo.tcProductCategoryUserSpecificTerm || "",
    tcProductDetailCode: productInfo.tcProductDetailCode || "",
    tcProductDetailUserSpecificTerm: productInfo.tcProductDetailUserSpecificTerm || "",
    
    // Raw Material Information
    tcProductRawMaterialInformation: (rawMaterialInfo || []).map((item: any) => ({
      tcProductRawMaterialCode: item.tcProductRawMaterialCode || "",
      tcProductRawMaterialPercentage: parseFloat(item.tcProductRawMaterialPercentage || "0"),
      tcProductRawMaterialUserSpecificTerm: item.tcProductRawMaterialUserSpecificTerm || "",
      tcProductRawMaterialCodeDescription: item.tcProductRawMaterialCodeDescription || ""
    })),

    // Geographic Information
    tcProductCertifiedRawMaterialGeographicInfo: (geographicInfo || []).map((item: any) => ({
      tcProductCertifiedRawMaterialCode: item.tcProductCertifiedRawMaterialCode || "",
      tcProductCertifiedRawMaterialCodeDescription: item.tcProductCertifiedRawMaterialCodeDescription || "",
      tcProductCertifiedRawMaterialWeight: parseFloat(item.tcProductCertifiedRawMaterialWeight || "0"),
      tcProductCertifiedRawMaterialCountryOrArea: item.tcProductCertifiedRawMaterialCountryOrArea || "",
      tcProductCertifiedRawMaterialCountryOrAreaDescription: item.tcProductCertifiedRawMaterialCountryOrAreaDescription || "",
      tcProductCertifiedRawMaterialStateOrProvince: item.tcProductCertifiedRawMaterialStateOrProvince || "",
      tcProductCertifiedRawMaterialStateOrProvinceDescription: item.tcProductCertifiedRawMaterialStateOrProvinceDescription || ""
    })),

    // Certified Weight Information
    tcProductCertifiedWeightInformation: (productCertifiedWeightInfo || []).map((item: any) => ({
      tcProductClaimsCategory: item.tcProductClaimsCategory || "",
      tcProductCertifiedWeight: parseFloat(item.tcProductCertifiedWeight || "0"),
      tcProductRawMaterialCodeClaimsCategory: item.tcProductRawMaterialCodeClaimsCategory || "",
      tcProductRawMaterialCodeClaimsCategoryDescription: item.tcProductRawMaterialCodeClaimsCategoryDescription || ""
    })),

    // Facilities Information
    tcProductFacilitiesInformation: (productFacilitiesInfo || []).map((item: any) => ({
      tcProductFacilitiesTeId: item.tcProductFacilitiesTeId || "",
      tcProductFacilitiesProcessCode: item.tcProductFacilitiesProcessCode || ""
    })),

    // First Processor Information
    tcProductFirstProcessorInformation: (productFirstProcessorInfo || []).map((item: any) => ({
      tcProductFirstProcessorTeId: item.tcProductFirstProcessorTeId || "",
      tcProductFirstProcessCode: item.tcProductFirstProcessCode || ""
    })),

    // Additional Information with proper type handling
    tcProductAdditives: Array.isArray(additionalInfo.tcProductAdditives) 
      ? additionalInfo.tcProductAdditives.join(", ") 
      : String(additionalInfo.tcProductAdditives || ""),
    tcProductAdditivesWeight: additionalInfo.tcProductAdditivesWeight !== null 
      ? parseFloat(String(additionalInfo.tcProductAdditivesWeight || "0"))
      : 0,
    tcProductInfoFiberLength: additionalInfo.tcProductInfoFiberLength || "",
    tcProductInfoYarnCount: additionalInfo.tcProductInfoYarnCount || "",
    tcProductInfoFabricWeightAndConstruct: additionalInfo.tcProductInfoFabricWeightAndConstruct || "",
    tcProductInfoFabricWeightOfFinishedProduct: additionalInfo.tcProductInfoFabricWeightOfFinishedProduct || "",
    tcProductInfoTechnicalSpecifications: additionalInfo.tcProductInfoTechnicalSpecifications || "",
    tcProductFeedstock: Array.isArray(additionalInfo.tcProductFeedStock)
      ? additionalInfo.tcProductFeedStock
      : (additionalInfo.tcProductFeedStock && String(additionalInfo.tcProductFeedStock).trim() ? [String(additionalInfo.tcProductFeedStock)] : []),
    tcProductPercentTextileWaste: additionalInfo.tcProductPercentTextileWaste || "",
    tcProductPackagingFor: additionalInfo.tcProductPackagingFor || "",
    tcProductGtin: additionalInfo.tcProductGtin || "",
    tcProductReclaimedMaterialSource: Array.isArray(additionalInfo.tcProductMaterialReclaimedSource)
      ? additionalInfo.tcProductMaterialReclaimedSource.map((item: string) => {
          const v = String(item).trim();
          const digitsPattern = /^\d{4}$/;
          if (digitsPattern.test(v)) return `PR${v}`;
          return v;
        })
      : (additionalInfo.tcProductMaterialReclaimedSource && String(additionalInfo.tcProductMaterialReclaimedSource).trim() ? [String(additionalInfo.tcProductMaterialReclaimedSource)] : []),
    tcProductInfoAdditional: additionalInfo.tcProductInfoAdditional || "",
    tcProductOriginFacilityTeId: Array.isArray(additionalInfo.tcProductOriginFacilityTeId)
      ? additionalInfo.tcProductOriginFacilityTeId
      : additionalInfo.tcProductOriginFacilityTeId
      ? [String(additionalInfo.tcProductOriginFacilityTeId)]
      : [],
    
    // Description fields (populate from stores if available)
    tcProductCategoryCodeDescription: "",
    tcProductDetailCodeDescription: "",

    // Include productId if editing
    ...(productId ? { productId } : {})
  };
};

/**
 * Function to get shipment ID based on a shipment number
 * Returns consistent string[] format
 */
// In certified-products.model.ts
// In certified-products.model.ts
export const getShipmentIdsByNumbers = (
  shipmentData: ApiShipmentResponseItem[],
  shipmentNo: string
): { shipmentId: string } | undefined => {
  if (!shipmentNo || !shipmentData || !Array.isArray(shipmentData)) {
    console.warn(`Invalid input for getShipmentIdsByNumbers: shipmentNo=${shipmentNo}, shipmentData=`, shipmentData);
    return undefined;
  }
  const shipment = shipmentData.find(
    (item) => item.tcShipmentInformation?.[0]?.tcShipmentDocNo === shipmentNo
  );
  if (!shipment) {
    console.warn(`No shipment found for shipmentNo: ${shipmentNo}`);
    return undefined;
  }
  return { shipmentId: shipment.shipmentId };
};

/**
 * Get shipment IDs based on a single product ID
 */
export const getShipmentIdsByProductId = (
  data: any[],
  productId: string
): string[] => {
  if (!productId || !data) return [];

  return data
    .filter((shipment) => shipment.productId === productId)
    .map((shipment) => shipment.shipmentId)
    .filter(Boolean);
};

/**
 * Maps product API response data to step data structure
 * Enhanced with proper type handling
 */
export const mapProductDataToStepData = (product: any, netShippingWeight: number): ProductFormData => {
  return {
    productInfo: {
      tcProductOrderNo: product.tcProductOrderNo || [],
      tcProductArticleNo: product.tcProductArticleNo || "",
      tcProductProductionDate: product.tcProductProductionDate || "",
      tcProductShipmentNo: product.tcProductShipmentNo || [],
      tcProductNumberOfUnits: product.tcProductNumberOfUnits || "",
      tcProductUnitsMetric: product.tcProductUnitsMetric || "",
      tcProductNetShippingWeight: netShippingWeight,
      tcProductSupplementaryWeight: product.tcProductSupplementaryWeight || 0,
      tcProductInfoSupplementaryWeight: product.tcProductInfoSupplementaryWeight || "",
      tcProductCategoryCode: product.tcProductCategoryCode || "",
      tcProductCategoryUserSpecificTerm: product.tcProductCategoryUserSpecificTerm || "",
      tcProductDetailCode: product.tcProductDetailCode || "",
      tcProductDetailUserSpecificTerm: product.tcProductDetailUserSpecificTerm || "",
      tcProductCategoryCodeDescription: product.tcProductCategoryCodeDescription || "",
      tcProductDetailCodeDescription: product.tcProductDetailCodeDescription || ""
    },
    geographicInfo: (product.tcProductCertifiedRawMaterialGeographicInformation || []).map((item: any) => ({
      tcProductCertifiedRawMaterialCode: item.tcProductCertifiedRawMaterialCode || "",
      tcProductCertifiedRawMaterialCodeDescription: item.tcProductCertifiedRawMaterialCodeDescription || "",
      tcProductCertifiedRawMaterialWeight: parseFloat(item.tcProductCertifiedRawMaterialWeight?.toString() || "0") || 0,
      tcProductCertifiedRawMaterialCountryOrArea: item.tcProductCertifiedRawMaterialCountryOrArea || "",
      tcProductCertifiedRawMaterialCountryOrAreaDescription: item.tcProductCertifiedRawMaterialCountryOrAreaDescription || "",
      tcProductCertifiedRawMaterialStateOrProvince: item.tcProductCertifiedRawMaterialStateOrProvince || "",
      tcProductCertifiedRawMaterialStateOrProvinceDescription: item.tcProductCertifiedRawMaterialStateOrProvinceDescription || "",
    })),
    additionalInfo: {
      tcProductInfoAdditional: product.tcProductInfoAdditional || "",
      tcProductInfoFabricWeightAndConstruct: product.tcProductInfoFabricWeightAndConstruct || "",
      tcProductInfoFabricWeightOfFinishedProduct: product.tcProductInfoFabricWeightOfFinishedProduct || "",
      tcProductInfoFiberLength: product.tcProductInfoFiberLength || "",
      tcProductInfoYarnCount: product.tcProductInfoYarnCount || "",
      tcProductAdditives: product.tcProductAdditives || "",
      tcProductAdditivesWeight: product.tcProductAdditivesWeight || 0,
      tcProductFeedstock: product.tcProductFeedstock || "",
      tcProductPercentTextileWaste: product.tcProductPercentTextileWaste || "",
      tcProductPackagingFor: product.tcProductPackagingFor || "",
      tcProductGtin: product.tcProductGtin || "",
      tcProductReclaimedMaterialSource: product.tcProductReclaimedMaterialSource || "",
      tcProductInfoTechnicalSpecifications: product.tcProductInfoTechnicalSpecifications || "",
      tcProductOriginFacilityTeId: Array.isArray(product.tcProductOriginFacilityTeId)
        ? product.tcProductOriginFacilityTeId
        : product.tcProductOriginFacilityTeId
        ? [product.tcProductOriginFacilityTeId]
        : []
    },
    rawMaterialInfo: product.tcProductRawMaterialInformation || [],
    productCertifiedWeightInfo: product.tcProductCertifiedWeightInformation || [],
    productFacilitiesInfo: product.tcProductFacilitiesInformation || [],
    productFirstProcessorInfo: product.tcProductFirstProcessorInformation || []
  };
};